import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { List } from 'immutable';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import Offer from '../../../containers/betslip/Offer';
import MarketingReminderPopup from '../../../containers/MarketingReminderPopup';
import {
  DOCUMENT_STATUSES,
  EDD_DOCUMENT_STATUSES_TO_VERIFY,
} from '@xanadu/ui-shared/modules/user/data';

import commonStyles from '../stylesheets/shared.module.scss';
import style from './Offers.module.scss';

const {
  NOT_SENT,
  REJECTED,
  ADDITIONAL_DOCS,
  RE_VERIFICATION_NEEDED,
} = DOCUMENT_STATUSES;

const Offers = ({
  onUpdateOffers,
  onRemoveOffers,
  onRemoveMultipleSelections,
  onSubmitOffers,
  onCancelOffers,
  onReviewOffer,
  currency,
  showBetConfirmation,
  bonusAvailable,
  showMarketingConsentReminder,
  proofOfAddressStatus: POAStatus,
  proofOfIdentityStatus: POIStatus,
  eddStatus,
  status,
  unsubmittedOffers: offers,
  showSettings,
  handleActiveTabLink
}) => {
  const scrollableElementRef = useRef();
  const [nextPage, setNextPage] = useState(1);

  // Priority 0
  const showEdd = EDD_DOCUMENT_STATUSES_TO_VERIFY.includes(eddStatus);

  // Priority 1
  const showNotSent = [POIStatus, POAStatus].includes(NOT_SENT)
    && !showEdd;

  // Priority 2
  const showRejected = [POIStatus, POAStatus].includes(REJECTED)
    && !showEdd && !showNotSent;

  // Priority 3
  const showAdditionalDocs = [POIStatus, POAStatus].includes(ADDITIONAL_DOCS)
    && !showEdd && !showRejected && !showNotSent;

  // Priority 4
  const showReVerify = [POIStatus, POAStatus].includes(RE_VERIFICATION_NEEDED)
    && !showEdd && !showRejected && !showAdditionalDocs && !showNotSent;

  const showPOIDoc =
    showNotSent && POIStatus === NOT_SENT
    || showRejected && POIStatus === REJECTED
    || showReVerify && POIStatus === RE_VERIFICATION_NEEDED;

  const showPOADoc =
    showNotSent && POAStatus === NOT_SENT
    || showRejected && POAStatus === REJECTED
    || showReVerify && POAStatus === RE_VERIFICATION_NEEDED;

  const showKycNotification = showPOIDoc || showPOADoc || showAdditionalDocs;

  const isOffersView = status === 'unsubmitted';

  const onUseBonusUpdate = (cid) => {
    offers.forEach((offer) => {
      if (offer.cid === cid) {
        onUpdateOffers({
          cid: offer.cid,
          id: offer.id,
          useBonus: false,
          bonusId: null,
        });
      }
      return offer;
    });
  };

  useEffect(() => {
    if (scrollableElementRef.current) {
      scrollableElementRef.current.addEventListener('scroll', () => {
        const {
          scrollHeight,
          scrollTop,
          clientHeight
        } = scrollableElementRef.current;

        if (Math.floor(scrollHeight - scrollTop) <= clientHeight) {
          setNextPage(page => page + 1);
        }
      });
    }
  }, [scrollableElementRef.current]);

  let bets = offers.sortBy(offer => {
    const unix = new Date(offer.createdAt).getTime();
    return -unix;
  });

  const offersClass = classNames({
    [style.offers]: true,
    [style[`offers--${status}`]]: true,
    [style['offers--settingsOpened']]: showSettings,
    [style['offers--empty']]: bets && bets.size === 0,
    [style['offers--unmatched']]: status === 'unmatched'
  });

  if (status === 'unmatched') {
    bets = offers
      .sortBy(offer => offer.remote.odds)
      .sortBy(offer => offer.side)
      .sortBy(offer => offer.marketId)
      .sortBy(offer => offer.eventId);
  }

  const groupedBets = bets.slice(0, 20 * (nextPage))
    .groupBy(({ eventId }) => eventId);

  return (
    <div className={offersClass}>
      <div className={style.containerOffers}>
        <div className={style.containerScrollable} ref={scrollableElementRef}>
          {showKycNotification || (showMarketingConsentReminder && isOffersView) && (
            <MarketingReminderPopup />
          )}
          {groupedBets.map((groupedOffers, eventId) => {
            const {
              eventName
            } = offers.find(offer => offer.eventId === eventId) || {};

            const betsUpdate = [
              // eslint-disable-next-line react/no-array-index-key
              <div className={commonStyles.containerHeader} key={eventId}>
                <Link
                  to={`/events/${eventId}`}
                  className={commonStyles.eventLink}
                  style={{ width: '90%' }}
                >
                  <span>{eventName}</span>
                </Link>
              </div>,
              ...groupedOffers.map((offer, index) => (
                <Offer
                  key={`offer_${offer.cid}`}
                  offer={offer}
                  onUpdateOffer={onUpdateOffers}
                  onRemoveOffer={onRemoveOffers}
                  onRemoveMultipleSelection={onRemoveMultipleSelections}
                  onSubmitOffer={onSubmitOffers}
                  onCancelOffer={onCancelOffers}
                  onReviewOffer={onReviewOffer}
                  onUseBonusUpdate={onUseBonusUpdate}
                  showBetConfirmation={showBetConfirmation}
                  currency={currency}
                  offerIndex={index}
                  handleActiveTabLink={handleActiveTabLink}
                />
              ))
            ];

            return betsUpdate;
          }).toArray()}
          {(bets).size === 0 && (
            <div className={style.noOffers}>
              <span>No Selections</span>
            </div>
          )}
          {bonusAvailable && (bets).size > 0 && isOffersView && (
            <div className={style.bonusLabel}>
              Stakes placed using Free Bets are not returned on winning bets.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Offers.propTypes = {
  onUpdateOffers: PropTypes.func.isRequired,
  onRemoveOffers: PropTypes.func,
  onRemoveMultipleSelections: PropTypes.func,
  handleActiveTabLink: PropTypes.func,
  onSubmitOffers: PropTypes.func,
  onCancelOffers: PropTypes.func,
  onReviewOffer: PropTypes.func,
  currency: PropTypes.string,
  showBetConfirmation: PropTypes.bool,
  bonusAvailable: PropTypes.bool,
  showSettings: PropTypes.bool,
  showMarketingConsentReminder: PropTypes.bool,
  status: PropTypes.string,
  unsubmittedOffers: PropTypes.instanceOf(List).isRequired,
  proofOfIdentityStatus: PropTypes.string,
  proofOfAddressStatus: PropTypes.string,
  eddStatus: PropTypes.string,
};

export default Offers;
